import type { Portfolio } from "@/types/Portfolio";
import { emptyNotifications } from "@/types/notifications";

/**
 * The one watchlist a demo visitor gets, for the asset class they picked.
 *
 * Logged-out visitors cannot have watchlists: GET /portfolios answers 403, and
 * creating one needs an account. So the filter and highlight panels came up
 * with no lists at all, and the guided run had nothing to point at when it
 * asked them to highlight something — the panel showed the full static
 * category list instead, which is every topic we publish rather than anything
 * to do with their market.
 *
 * This supplies a real one instead. It is a plain object, never persisted, and
 * it exists only while an asset class is selected.
 *
 * Lives here rather than under src/demo-guide/ on purpose. usePortfoliosCRUD
 * is reached from src/widget-components/**, which renders inside popout
 * windows, and nothing under demo-guide may be. So the guide writes into this
 * module and the hook reads from it; neither imports the other. Same
 * subscribe/snapshot shape as ./prebuiltWatchlists, for the same reason.
 */
export type DemoWatchlistSpec = {
  name: string;
  tickers: string[];
  keywords: string[];
  /**
   * Tag ids, when the list came from a recorded plan.
   *
   * A recorded combination leans on tags as much as on terms — the FX plan is
   * five lists and ten tags — so dropping them would hand the demo a visibly
   * thinner version of the watchlist it just showed being built.
   */
  tags?: string[];
};

/**
 * Negative so it can never collide with a real pref-storage id, and stable so
 * a selection survives re-renders.
 */
export const DEMO_WATCHLIST_ID = -1;

let current: Portfolio | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function setDemoWatchlist(spec: DemoWatchlistSpec | null): void {
  current = spec
    ? {
        id: DEMO_WATCHLIST_ID,
        name: spec.name,
        tickers: spec.tickers,
        keywords: spec.keywords,
        ...(spec.tags?.length ? { tags: spec.tags } : {}),
        color: "#00aeef",
        notifications: emptyNotifications(),
        customizations: { includeRelatedHeadlines: false },
        // NEWSQUAWK rather than USER: it is ours, it is read-only, and the
        // builder's edit affordances key off owner_type.
        owner_type: "NEWSQUAWK",
      }
    : null;
  emit();
}

export function subscribeToDemoWatchlist(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getDemoWatchlistSnapshot(): Portfolio | null {
  return current;
}
